import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { transferenciaService, Transferencia } from '../../services/transferenciaService';
import { sucursalService } from '../../services/sucursalService';
import TransferenciaForm from '../../components/Transferencias/TransferenciaForm';
import TransferenciaDetail from '../../components/Transferencias/TransferenciaDetail';

const TransferenciasList = () => {
  const [page, setPage] = useState(1);
  const [estadoFilter, setEstadoFilter] = useState<string>('');
  const [sucursalFilter, setSucursalFilter] = useState<number | undefined>(undefined);
  const [showForm, setShowForm] = useState(false);
  const [selectedTransferencia, setSelectedTransferencia] = useState<Transferencia | null>(null);
  const queryClient = useQueryClient();

  const { data: sucursalesData } = useQuery(
    'sucursales',
    () => sucursalService.getAll(1, 100)
  );

  const { data, isLoading } = useQuery(
    ['transferencias', page, estadoFilter, sucursalFilter],
    () => transferenciaService.getAll(page, 20, estadoFilter || undefined, sucursalFilter)
  );

  const cancelarMutation = useMutation(transferenciaService.cancelar, {
    onSuccess: () => {
      toast.success('Transferencia cancelada correctamente');
      queryClient.invalidateQueries(['transferencias']);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al cancelar transferencia');
    }
  });

  const handleCancelar = (id: number) => {
    if (window.confirm('¿Estás seguro de cancelar esta transferencia?')) {
      cancelarMutation.mutate(id);
    }
  };

  const handleView = (transferencia: Transferencia) => {
    setSelectedTransferencia(transferencia);
  };

  const handleCloseForm = () => {
    setShowForm(false);
  };

  const handleCloseDetail = () => {
    setSelectedTransferencia(null);
  };

  if (isLoading) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  const estados = [
    { value: '', label: 'Todos' },
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'en_transito', label: 'En Tránsito' },
    { value: 'recibida', label: 'Recibida' },
    { value: 'cancelada', label: 'Cancelada' }
  ];

  const getEstadoColor = (estado: string) => {
    const colors: { [key: string]: string } = {
      pendiente: 'bg-yellow-100 text-yellow-800',
      en_transito: 'bg-blue-100 text-blue-800',
      recibida: 'bg-green-100 text-green-800',
      cancelada: 'bg-red-100 text-red-800'
    };
    return colors[estado] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Transferencias entre Sucursales</h1>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition"
        >
          + Nueva Transferencia
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b space-y-4">
          <div className="flex space-x-4">
            <select
              value={estadoFilter}
              onChange={(e) => {
                setEstadoFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {estados.map((estado) => (
                <option key={estado.value} value={estado.value}>
                  {estado.label}
                </option>
              ))}
            </select>
            <select
              value={sucursalFilter || ''}
              onChange={(e) => {
                setSucursalFilter(e.target.value ? Number(e.target.value) : undefined);
                setPage(1);
              }}
              className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Todas las sucursales</option>
              {sucursalesData?.data.sucursales.map((sucursal) => (
                <option key={sucursal.id} value={sucursal.id}>
                  {sucursal.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Origen</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destino</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Envío</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Recepción</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data?.data.transferencias.map((transferencia) => (
                <tr key={transferencia.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#{transferencia.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {transferencia.sucursalOrigen?.name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {transferencia.sucursalDestino?.name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getEstadoColor(transferencia.estado)}`}>
                      {transferencia.estado.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {transferencia.fechaEnvio ? new Date(transferencia.fechaEnvio).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {transferencia.fechaRecepcion ? new Date(transferencia.fechaRecepcion).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleView(transferencia)}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      Ver
                    </button>
                    {(transferencia.estado === 'pendiente' || transferencia.estado === 'en_transito') && (
                      <button
                        onClick={() => handleCancelar(transferencia.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Cancelar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data?.data.pagination && (
          <div className="p-4 border-t flex justify-between items-center">
            <div className="text-sm text-gray-700">
              Mostrando {((page - 1) * 20) + 1} a {Math.min(page * 20, data.data.pagination.total)} de {data.data.pagination.total}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= data.data.pagination.totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {showForm && (
        <TransferenciaForm
          onClose={handleCloseForm}
        />
      )}

      {selectedTransferencia && (
        <TransferenciaDetail
          transferencia={selectedTransferencia}
          onClose={handleCloseDetail}
        />
      )}
    </div>
  );
};

export default TransferenciasList;

